/**
 * Operator portal rework PR 1 — hooks over /v1/platform/operator-companies: the operator company
 * linked to a catalog operator, and its users (lx-coord/operator-portal-design.md §2.1).
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  authedFetch,
  useApiMutation,
  useApiOpts,
  useApiQuery,
  type EmptyBody,
} from '@lodex-pro/web-shared';
import { OPERATOR_PLAN_KEY } from './plan-hooks';

export const OPERATOR_TIER_OPTIONS = ['FREE', 'BASIC', 'PREMIUM', 'ENTERPRISE'] as const;
export type OperatorTierOption = (typeof OPERATOR_TIER_OPTIONS)[number];

export const OPERATOR_USER_ROLE_OPTIONS = [
  { value: 'OPERATOR_ADMIN', label: 'Admin' },
  { value: 'FOREMAN', label: 'Foreman' },
  { value: 'PUMPER', label: 'Pumper' },
] as const;
export type OperatorUserRole = (typeof OPERATOR_USER_ROLE_OPTIONS)[number]['value'];

export interface OperatorCompany {
  readonly id: string;
  readonly name: string;
  readonly tier: OperatorTierOption;
  readonly platformOperatorId: string;
  readonly createdAt: string;
}

export interface OperatorCompanyUser {
  readonly userId: string;
  readonly fullName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly invited: boolean;
  readonly role: OperatorUserRole;
  readonly status: 'ACTIVE' | 'SUSPENDED' | 'OFFBOARDED';
  readonly activatedAt: string | null;
  /** OPP PR 11b: when a pumper's first SMS sign-in proved the phone; null = not yet. */
  readonly phoneConfirmedAt?: string | null;
  readonly joinedAt: string;
  /**
   * Migration 0227 — SES flagged the address (BOUNCING after a hard bounce, COMPLAINED after a
   * spam complaint): no invite goes to it. Absent (an older API) means OK.
   */
  readonly emailStatus?: 'OK' | 'BOUNCING' | 'COMPLAINED';
}

export interface CreateOperatorUserBody {
  fullName: string;
  email: string;
  phone?: string;
  role: OperatorUserRole;
}

const COMPANY_KEY = (platformOperatorId: string): readonly string[] => [
  'platform',
  'operator-companies',
  platformOperatorId,
];
const USERS_KEY = (tenantId: string): readonly string[] => [
  'platform',
  'operator-companies',
  tenantId,
  'users',
];

export function useOperatorCompany(
  platformOperatorId: string,
): ReturnType<typeof useApiQuery<{ company: OperatorCompany | null }>> {
  const opts = useApiOpts();
  return useApiQuery<{ company: OperatorCompany | null }>(
    opts,
    `/v1/platform/operator-companies?platformOperatorId=${platformOperatorId}`,
    { queryKey: COMPANY_KEY(platformOperatorId), staleTime: 15_000 },
  );
}

export function useCreateOperatorCompany(
  platformOperatorId: string,
): ReturnType<
  typeof useApiMutation<
    { platformOperatorId: string; tier: OperatorTierOption },
    { company: OperatorCompany }
  >
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<
    { platformOperatorId: string; tier: OperatorTierOption },
    { company: OperatorCompany }
  >(opts, '/v1/platform/operator-companies', 'POST', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COMPANY_KEY(platformOperatorId) });
    },
  });
}

export function useOperatorCompanyUsers(
  tenantId: string,
): ReturnType<typeof useApiQuery<{ users: readonly OperatorCompanyUser[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ users: readonly OperatorCompanyUser[] }>(
    opts,
    `/v1/platform/operator-companies/${tenantId}/users`,
    { queryKey: USERS_KEY(tenantId), staleTime: 10_000 },
  );
}

export function useCreateOperatorUser(
  tenantId: string,
): ReturnType<
  typeof useApiMutation<CreateOperatorUserBody, { userId: string; inviteEmailed: boolean }>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CreateOperatorUserBody, { userId: string; inviteEmailed: boolean }>(
    opts,
    `/v1/platform/operator-companies/${tenantId}/users`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: USERS_KEY(tenantId) });
        // OPP PR 6 — the Plan card counts active users against the limit.
        void qc.invalidateQueries({ queryKey: OPERATOR_PLAN_KEY(tenantId) });
      },
    },
  );
}

export function useUpdateOperatorUser(
  tenantId: string,
  userId: string,
): ReturnType<
  typeof useApiMutation<
    { role?: OperatorUserRole; status?: 'ACTIVE' | 'SUSPENDED' },
    { userId: string; role: string; status: string }
  >
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<
    { role?: OperatorUserRole; status?: 'ACTIVE' | 'SUSPENDED' },
    { userId: string; role: string; status: string }
  >(opts, `/v1/platform/operator-companies/${tenantId}/users/${userId}`, 'PATCH', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: USERS_KEY(tenantId) });
      void qc.invalidateQueries({ queryKey: OPERATOR_PLAN_KEY(tenantId) });
    },
  });
}

export interface ResendInviteResult {
  readonly userId: string;
  readonly inviteEmailed: boolean;
  /** 0227 — no email went: the address is flagged. */
  readonly inviteEmailSkipped?: 'EMAIL_BOUNCING' | null;
  /** #936 review MED-1 — a phone-first pumper's account was set up all the same. */
  readonly accountReady?: boolean;
}

/** #936 review L1 — Paelode clears a hard-bounce flag (BOUNCING → OK); a complaint stays. */
export function useClearOperatorUserEmailFlag(
  tenantId: string,
  userId: string,
): ReturnType<typeof useApiMutation<EmptyBody, { userId: string; emailStatus: 'OK' }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<EmptyBody, { userId: string; emailStatus: 'OK' }>(
    opts,
    `/v1/platform/operator-companies/${tenantId}/users/${userId}/clear-email-flag`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: USERS_KEY(tenantId) });
      },
    },
  );
}

export function useResendOperatorInvite(
  tenantId: string,
  userId: string,
): ReturnType<typeof useApiMutation<EmptyBody, ResendInviteResult>> {
  const opts = useApiOpts();
  return useApiMutation<EmptyBody, ResendInviteResult>(
    opts,
    `/v1/platform/operator-companies/${tenantId}/users/${userId}/invite`,
    'POST',
  );
}

// --- Operator portal rework PR 5 — view as operator (routes/platform/view-as-sessions.ts) ---

export interface StartViewAsBody {
  operatorTenantId: string;
  targetUserId?: string;
  reason: string;
}
export interface StartViewAsResponse {
  sessionId: string;
  portalUrl: string;
  expiresAt: string;
}
export interface ViewAsSessionRow {
  id: string;
  adminName: string | null;
  adminEmail: string | null;
  targetName: string | null;
  targetEmail: string | null;
  reason: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  endedReason: string | null;
  readCount: number;
  status: 'LIVE' | 'ENDED' | 'EXPIRED';
}

const VIEW_AS_KEY = (tenantId: string): readonly string[] => [
  'platform',
  'view-as-sessions',
  tenantId,
];

/** Start a view-as session; the response's portalUrl opens the portal as the target. */
export function useStartViewAs(
  tenantId: string,
): ReturnType<typeof useApiMutation<StartViewAsBody, StartViewAsResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<StartViewAsBody, StartViewAsResponse>(
    opts,
    '/v1/platform/view-as-sessions',
    'POST',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: VIEW_AS_KEY(tenantId) }) },
  );
}

/** The company's view-as audit: who looked, as whom, why, when, how many reads. */
export function useViewAsSessions(
  tenantId: string,
): ReturnType<typeof useApiQuery<{ sessions: ViewAsSessionRow[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ sessions: ViewAsSessionRow[] }>(
    opts,
    `/v1/platform/view-as-sessions?operatorTenantId=${tenantId}`,
    { queryKey: VIEW_AS_KEY(tenantId), staleTime: 15_000 },
  );
}

export function useEndViewAs(
  tenantId: string,
  sessionId: string,
): ReturnType<typeof useApiMutation<EmptyBody, unknown>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<EmptyBody, unknown>(
    opts,
    `/v1/platform/view-as-sessions/${sessionId}/end`,
    'POST',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: VIEW_AS_KEY(tenantId) }) },
  );
}

/** Plain words for the errors these routes answer. */
export function operatorPortalErrorMessage(err: unknown): string {
  // web-shared's ApiError carries the API's error.code; read it by shape.
  const code =
    typeof err === 'object' && err !== null && typeof (err as { code?: unknown }).code === 'string'
      ? (err as { code: string }).code
      : null;
  switch (code) {
    case 'USER_ALREADY_EXISTS':
      return 'Someone with that email or phone already has a lodeX Pro account.';
    case 'OPERATOR_TENANT_ALREADY_EXISTS':
      return 'This operator already has a portal company.';
    case 'PLATFORM_OPERATOR_NOT_FOUND':
      return 'This operator is inactive or merged, so it cannot get a portal.';
    case 'COGNITO_USER_CREATION_FAILED':
    case 'COGNITO_USER_UPDATE_FAILED':
      return 'The sign-in account could not be updated. Try again in a minute.';
    case 'VIEW_AS_TARGET_INVALID':
      return 'That user is not active in this company, so the portal cannot be viewed as them.';
    case 'SEAT_LIMIT_REACHED':
      // OPP PR 6 (0212) — the company's user limit; raised on its Plan card.
      return 'This company is at its user limit. Raise the limit on its Plan, or suspend someone first.';
    case 'VALIDATION_FAILED':
      return 'Check the fields: a pumper needs a 10-digit US phone number, and the email must be valid.';
    default:
      return 'That did not work. Try again, or check the API logs.';
  }
}

// ─── OPP PR 2: bulk import + "Send invites (N)" ──────────────────────────────

export type ImportRowStatus = 'NEW' | 'UPDATE' | 'ROLE_CHANGE' | 'UNCHANGED' | 'ERROR';

export interface ImportRow {
  readonly rowNumber: number;
  readonly fullName: string;
  readonly email: string;
  readonly phone: string | null;
  readonly userTypeRaw: string;
  readonly role: OperatorUserRole | null;
  readonly status: ImportRowStatus;
  readonly error?: string;
  readonly detail?: string;
  readonly fromRole?: OperatorUserRole;
}

export interface ImportResponse {
  readonly applied: boolean;
  readonly sheet: string;
  readonly summary: {
    readonly total: number;
    readonly new: number;
    readonly update: number;
    readonly roleChange: number;
    readonly unchanged: number;
    readonly errors: number;
  };
  readonly rows: readonly ImportRow[];
  readonly result?: {
    readonly created: number;
    readonly updated: number;
    readonly rolesChanged: number;
    readonly cognitoSyncFailed: readonly string[];
  };
}

/** Why the server refused the whole file, from its VALIDATION_FAILED fields, else a generic line. */
async function importErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as {
      error?: { code?: string; fields?: Array<{ path?: string; reason?: string }> };
    };
    const reason = body.error?.fields?.find((f) => f.path === 'file')?.reason;
    if (reason !== undefined) return reason;
    if (body.error?.code === 'PAYLOAD_TOO_LARGE') return 'That file is too large.';
  } catch {
    // Not JSON; fall through.
  }
  return 'The file could not be read. Use a CSV or XLSX with name, email, phone and user type.';
}

/**
 * Upload the file for a preview (apply false) or to write it (apply true, with the emails whose
 * role change Paelode ticked). The server re-reads the file on apply; nothing from the preview is
 * trusted.
 */
export async function importOperatorUsers(
  opts: ReturnType<typeof useApiOpts>,
  tenantId: string,
  file: File,
  args: { apply: boolean; roleChanges?: readonly string[] },
): Promise<ImportResponse> {
  const form = new FormData();
  if (args.roleChanges !== undefined) form.append('roleChanges', JSON.stringify(args.roleChanges));
  form.append('file', file, file.name);
  const res = await authedFetch(
    opts,
    `/v1/platform/operator-companies/${tenantId}/users/import${args.apply ? '?apply=true' : ''}`,
    { method: 'POST', body: form },
  );
  if (!res.ok) throw new Error(await importErrorMessage(res));
  return (await res.json()) as ImportResponse;
}

/** One "Send invites (N)" call's answer: at most a batch invited, and how many are still pending. */
export interface InviteBatchResult {
  readonly invited: number;
  readonly emailFailed: number;
  readonly inProgressElsewhere: number;
  readonly failed: readonly { readonly userId: string; readonly code: string }[];
  readonly pending: number;
  /** 0227 — not invited and not invitable: their address is flagged. */
  readonly emailBouncing?: number;
  /** #936 review MED-1 — phone-first pumpers set up for the app, their email skipped. */
  readonly phoneOnly?: number;
}

/** Totals over every batch of one "Send invites (N)" press. */
export interface InviteRunTotals {
  readonly invited: number;
  readonly emailFailed: number;
  readonly failed: number;
  readonly pending: number;
  readonly emailBouncing: number;
  readonly phoneOnly: number;
}

/**
 * #897 review (MED): the server invites at most a small batch per call (a whole company in one
 * request outran the load balancer's 60 s), so the screen calls again until nobody is pending,
 * or a call invites nobody (only failures, or another press already inviting them). Each person
 * is claimed on the server before any account is made, so overlapping presses never double-invite.
 */
export async function sendPendingInvites(
  opts: ReturnType<typeof useApiOpts>,
  tenantId: string,
  onProgress: (totals: InviteRunTotals) => void,
): Promise<InviteRunTotals> {
  let totals: InviteRunTotals = {
    invited: 0,
    emailFailed: 0,
    failed: 0,
    pending: 0,
    emailBouncing: 0,
    phoneOnly: 0,
  };
  // A hard ceiling: 500 people / 20 per call = 25 calls; never loop forever.
  for (let call = 0; call < 40; call += 1) {
    const res = await authedFetch(
      opts,
      `/v1/platform/operator-companies/${tenantId}/users/invite-pending`,
      { method: 'POST', body: JSON.stringify({}) },
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: { code?: string } };
      throw Object.assign(new Error(`invite-pending failed (${String(res.status)})`), {
        code: body.error?.code ?? null,
      });
    }
    const r = (await res.json()) as InviteBatchResult;
    totals = {
      invited: totals.invited + r.invited,
      emailFailed: totals.emailFailed + r.emailFailed,
      failed: totals.failed + r.failed.length,
      pending: r.pending,
      emailBouncing: r.emailBouncing ?? 0,
      phoneOnly: totals.phoneOnly + (r.phoneOnly ?? 0),
    };
    onProgress(totals);
    if (r.pending === 0 || r.invited === 0) break;
  }
  return totals;
}

/** Invalidate a company's user list (after an import applied). */
export function useRefreshOperatorUsers(tenantId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: USERS_KEY(tenantId) });
}
